"use client";
import { useState, useEffect } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import { signIn, useSession } from "next-auth/react";

const MAGIC_LINK_ERRORS: Record<string, string> = {
  invalid_token: "This sign-in link is invalid. Please request a new one or sign in below.",
  expired_token: "This sign-in link has expired (links are valid for 48 hours). Please sign in below.",
  user_not_found: "Account not found. Please sign in below or contact info@schedly.io.",
  not_partner: "This account doesn't have an active partner record. Contact partners@schedly.io.",
  server_error: "Something went wrong. Please try signing in below or contact info@schedly.io.",
};

export default function PartnerLogin() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Show magic link error from query param
  useEffect(() => {
    const { error: queryError } = router.query;
    if (typeof queryError === "string" && MAGIC_LINK_ERRORS[queryError]) {
      setError(MAGIC_LINK_ERRORS[queryError]);
      // Remove the error param from the URL without a full navigation
      router.replace("/partner/login", undefined, { shallow: true });
    }
  }, [router.query]); // eslint-disable-line react-hooks/exhaustive-deps

  // If already logged in, check partner status and redirect
  useEffect(() => {
    if (status === "authenticated" && session?.user) {
      fetch("/api/partner/dashboard")
        .then((r) => {
          if (r.ok) router.replace("/partner/portal");
          else setError("Your account doesn't have an active partner record. Check your approval email or contact info@schedly.io.");
        })
        .catch(() => setError("Could not verify partner status."));
    }
  }, [status, session, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const result = await signIn("credentials", {
      email: email.trim().toLowerCase(),
      password,
      redirect: false,
    });

    if (result?.error) {
      setError("Incorrect email or password. Please try again.");
      setLoading(false);
      return;
    }

    // Verify they actually have an active partner account
    const dashRes = await fetch("/api/partner/dashboard");
    if (dashRes.ok) {
      router.replace("/partner/portal");
    } else {
      setError("Logged in, but no active partner account found. Check your approval email or contact partners@schedly.io.");
      setLoading(false);
    }
  };

  return (
    <>
      <Head>
        <title>Partner Login — Schedly</title>
        <meta name="robots" content="noindex" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
        <style dangerouslySetInnerHTML={{ __html: `
          * { font-family: 'Inter', system-ui, sans-serif; box-sizing: border-box; }
          .blob { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; animation: blob-morph 8s ease-in-out infinite; }
          @keyframes blob-morph { 0%,100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; } 50% { border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%; } }
          @keyframes spin { to { transform: rotate(360deg); } }
          .field-input:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.15) !important; outline: none; }
          .btn-login { background: linear-gradient(135deg,#6366f1,#4f46e5); box-shadow: 0 4px 16px rgba(99,102,241,0.35); transition: all 0.2s ease; }
          .btn-login:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(99,102,241,0.5); }
          .btn-login:disabled { opacity: 0.65; cursor: not-allowed; }
          @media (max-width: 800px) { .portal-left { display: none !important; } }
        ` }} />
      </Head>

      <div style={{ minHeight: "100vh", display: "flex", fontFamily: "Inter,system-ui,sans-serif" }}>

        {/* ── LEFT PANEL ─────────────────────────────────────────── */}
        <div className="portal-left" style={{ width: 440, flexShrink: 0, background: "linear-gradient(160deg,#0f0c29,#1a1040,#302b63)", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column", padding: "52px 44px" }}>
          <div className="blob" style={{ position: "absolute", top: "10%", right: "-15%", width: 300, height: 300, background: "rgba(99,102,241,0.15)", filter: "blur(60px)" }} />
          <div className="blob" style={{ position: "absolute", bottom: "15%", left: "-10%", width: 240, height: 240, background: "rgba(124,58,237,0.12)", filter: "blur(50px)", animationDelay: "-4s" }} />

          <div style={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column" }}>
            <a href="https://schedly.io" style={{ display: "inline-block", marginBottom: 56 }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/schedly-logo-white.png" alt="Schedly" style={{ height: 28, opacity: 0.95 }} />
            </a>

            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", background: "rgba(99,102,241,0.2)", border: "1px solid rgba(165,180,252,0.25)", borderRadius: 20, color: "#a5b4fc", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 16, width: "fit-content" }}>
              🔐 PARTNER PORTAL
            </span>

            <h1 style={{ color: "#fff", fontSize: 34, fontWeight: 900, letterSpacing: "-0.025em", lineHeight: 1.15, margin: "0 0 16px" }}>
              Welcome back,<br />partner.
            </h1>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 15, lineHeight: 1.65, margin: "0 0 48px" }}>
              Manage your clients, monitor billing, and configure your white-label platform — all from one place.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[
                { icon: "👥", text: "Manage all your client accounts" },
                { icon: "📊", text: "Real-time billing & usage stats" },
                { icon: "🎨", text: "White-label branding controls" },
                { icon: "🔌", text: "API keys & webhook settings" },
                { icon: "🎫", text: "Support tickets & documentation" },
              ].map((item) => (
                <div key={item.text} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 9, background: "rgba(99,102,241,0.18)", border: "1px solid rgba(165,180,252,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
                    {item.icon}
                  </div>
                  <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, lineHeight: 1.4 }}>{item.text}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: "auto", paddingTop: 40 }}>
              <div style={{ padding: 16, background: "rgba(255,255,255,0.04)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.07)" }}>
                <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 12, lineHeight: 1.6 }}>
                  Not a partner yet?{" "}
                  <a href="/partner/apply" style={{ color: "#a5b4fc", textDecoration: "none", fontWeight: 600 }}>Apply here →</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ─────────────────────────────────────────── */}
        <div style={{ flex: 1, background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 24px" }}>
          <div style={{ width: "100%", maxWidth: 420 }}>

            {/* Mobile logo */}
            <a href="https://schedly.io" style={{ display: "inline-block", marginBottom: 32 }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/schedly-logo.png" alt="Schedly" style={{ height: 26, width: "auto" }} />
            </a>

            <h2 style={{ color: "#111827", fontSize: 26, fontWeight: 900, letterSpacing: "-0.02em", margin: "0 0 6px" }}>Partner portal</h2>
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 32px" }}>Sign in with your Schedly partner account.</p>

            <form onSubmit={handleSubmit}>
              <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 18, padding: 28, boxShadow: "0 4px 20px rgba(0,0,0,0.04)", marginBottom: 16 }}>

                {error && (
                  <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 14px", marginBottom: 20, display: "flex", gap: 10, alignItems: "flex-start" }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="8" cy="8" r="7" stroke="#ef4444" strokeWidth="1.5"/><path d="M8 5v3M8 10.5v.5" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    <span style={{ color: "#b91c1c", fontSize: 13, lineHeight: 1.5 }}>{error}</span>
                  </div>
                )}

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
                    Email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    autoComplete="email"
                    className="field-input"
                    style={{ width: "100%", padding: "11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 14, color: "#111827", background: "#fff", fontFamily: "inherit" }}
                  />
                </div>

                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <label style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Password</label>
                    <a href="/partner/forgot-password" style={{ fontSize: 12, color: "#6366f1", textDecoration: "none", fontWeight: 600 }}>Forgot password?</a>
                  </div>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      autoComplete="current-password"
                      className="field-input"
                      style={{ width: "100%", padding: "11px 40px 11px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 14, color: "#111827", background: "#fff", fontFamily: "inherit" }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex" }}>
                      {showPw
                        ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/></svg>
                      }
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-login"
                  style={{ width: "100%", padding: "13px", borderRadius: 11, border: "none", color: "#fff", fontSize: 15, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, letterSpacing: "-0.01em" }}>
                  {loading ? (
                    <>
                      <div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in to Partner Portal
                      <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </>
                  )}
                </button>
              </div>
            </form>

            <p style={{ textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
              Not a partner?{" "}
              <a href="/partner/apply" style={{ color: "#4f46e5", fontWeight: 600, textDecoration: "none" }}>Apply now →</a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
